/** Six-month net headline over one diverging bar per month off a zero baseline. */
import React from 'react';
import { StyleSheet, Text, View } from 'react-native';
import { fmtMoney } from '../../features/dashboard/utils';
import { useSharedPalette } from '../theme';
import { summariseProfitLoss, type ProfitLossMonth } from './homeModels';

export function ProfitLossChart({ months,compactWhenEmpty=false,pending=false,compact=false }: { months: ProfitLossMonth[];compactWhenEmpty?:boolean;pending?:boolean;compact?:boolean }) {
  const palette = useSharedPalette();
  const summary = summariseProfitLoss(months);
  // Calendar labels are known before accounting figures; no zero-valued data is fabricated.
  const now=new Date();
  const axis=pending?Array.from({length:6},(_,index)=>{const date=new Date(Date.UTC(now.getUTCFullYear(),now.getUTCMonth()-5+index,1));return {monthKey:date.toISOString().slice(0,7),label:date.toLocaleString('en-GB',{month:'short',timeZone:'UTC'})};}):summary.bars;
  const positive = summary.periodNet >= 0;
  const headlineColour = !pending&&summary.hasActivity ? (positive ? palette.success : palette.danger) : palette.muted;

  return (
    <View style={[styles.wrap, compact && {paddingBottom: 8}]}>
      <Text style={[styles.headline, { color: headlineColour }]}>
        {pending?'':`${positive?'':'-'}${fmtMoney(Math.abs(summary.periodNet))}`}
      </Text>
      <Text style={[styles.sub, { color: palette.muted }]}>
        {pending?'6-month net':summary.hasActivity
          ? `6-month net · ${fmtMoney(summary.periodIncome)} in, ${fmtMoney(summary.periodExpenses)} out`
          : 'No activity in the last 6 months'}
      </Text>
      {(pending||!compactWhenEmpty||summary.hasActivity)&&<><View style={[styles.plot, compact && {height: 64}]}>
        <View style={[styles.baseline, { top: `${pending?50:summary.zeroTopPercent}%`, backgroundColor: palette.border }]} />
        <View style={styles.columns}>
          {!pending&&summary.bars.map((bar) => (
            <View key={bar.monthKey} style={styles.column}>
              {bar.heightPercent > 0 && (
                <View
                  style={[
                    styles.bar,
                    {
                      top: `${bar.topPercent}%`,
                      height: `${bar.heightPercent}%`,
                      backgroundColor: bar.up ? palette.success : palette.danger,
                    },
                  ]}
                />
              )}
            </View>
          ))}
        </View>
      </View>
      <View style={styles.labels}>
        {axis.map((bar) => (
          <Text key={bar.monthKey} style={[styles.label, { color: palette.muted }]}>{bar.label}</Text>
        ))}
      </View></>}
    </View>
  );
}

const styles = StyleSheet.create({
  wrap: { paddingHorizontal: 20, paddingTop: 4, paddingBottom: 12 },
  headline: { fontSize: 18, fontWeight: '600', lineHeight: 22, minHeight: 22 },
  sub: { fontSize: 11, marginTop: 4, marginBottom: 10 },
  plot: { height: 82 },
  baseline: { position: 'absolute', left: 0, right: 0, height: StyleSheet.hairlineWidth },
  columns: { ...StyleSheet.absoluteFillObject, flexDirection: 'row', gap: 12 },
  column: { flex: 1, alignItems: 'center' },
  bar: { position: 'absolute', width: '100%', maxWidth: 24, borderRadius: 5 },
  labels: { flexDirection: 'row', gap: 12, marginTop: 6 },
  label: { flex: 1, textAlign: 'center', fontSize: 10 },
});
